import moment from 'moment-timezone';
import { IShipWindow } from '../../../types/IFilterEngine';
import { formatDateByMonthDayYear } from '../../PreOrders/Utils';

const shipDateKeyFormat = 'YYYY-MM-DD';

const normalizeShipDate = (date?: string) => (date ? moment(date).format(shipDateKeyFormat) : '');

// the dates are normalized to the day, so windows that differ only in their time are the same one
export const getShipWindowKey = (shippingStartDate?: string, shippingEndDate?: string) =>
  `${normalizeShipDate(shippingStartDate)}|${normalizeShipDate(shippingEndDate)}`;

// one option per distinct ship window, ordered by the date it starts on
export const getUniqueShipWindows = (items?: any[] | null): IShipWindow[] => {
  if (!items?.length) return [];
  const windowsByKey: { [key: string]: IShipWindow } = {};

  items.forEach((item: any) => {
    const { shippingStartDate, shippingEndDate } = item ?? {};
    if (!shippingStartDate && !shippingEndDate) return;
    const key = getShipWindowKey(shippingStartDate, shippingEndDate);
    if (windowsByKey[key]) return;
    windowsByKey[key] = {
      key,
      shippingStartDate,
      shippingEndDate,
      label: formatDateByMonthDayYear(shippingStartDate, shippingEndDate),
    };
  });

  return Object.values(windowsByKey).sort((a, b) => a.key.localeCompare(b.key));
};

// the filter engine's own name for a row that carries no value for the attribute
export const blankShipWindow = '(Blanks)';

export const getShipWindowFilterKey = (item: any) =>
  item?.shippingStartDate || item?.shippingEndDate
    ? getShipWindowKey(item.shippingStartDate, item.shippingEndDate)
    : blankShipWindow;

// the engine reads its options and its counts off a literal field, so every row is tagged in place
export const addShipWindowFilterData = (items?: any[] | null) => {
  const shipWindowLabels: { [key: string]: string } = {};
  const windowKeys = new Set<string>();
  let hasBlanks = false;

  items?.forEach((item: any) => {
    const key = getShipWindowFilterKey(item);
    item.shipWindow = key;
    if (key === blankShipWindow) {
      hasBlanks = true;
      return;
    }
    if (windowKeys.has(key)) return;
    windowKeys.add(key);
    shipWindowLabels[key] = formatDateByMonthDayYear(item.shippingStartDate, item.shippingEndDate);
  });

  const uniqueShipWindows = [...windowKeys].sort();
  if (hasBlanks) uniqueShipWindows.push(blankShipWindow);
  return { uniqueShipWindows, shipWindowLabels };
};

// the tabs are fetched one by one, so the windows of one must not drop the windows of another
export const mergeShipWindows = (previous?: string[] | null, next?: string[] | null) =>
  [...new Set([...(previous ?? []), ...(next ?? [])])].sort();

export const withShipWindowChipsData = (chipsDataState: any, shipWindowLabels: any) => ({
  ...chipsDataState,
  attributeLabels: { ...chipsDataState?.attributeLabels, shipWindow: 'Ship Dates' },
  shipWindowLabels,
});

export const matchesShipWindowFilter = (item: any, appliedShipWindows?: any) =>
  !appliedShipWindows || !!appliedShipWindows[getShipWindowFilterKey(item)];

export const shipWindowPalette = [
  { color: '#2F50AC', backgroundColor: '#E8EDF9' },
  { color: '#7A3AC7', backgroundColor: '#F0E9FB' },
  { color: '#4A7A22', backgroundColor: '#ECF4E4' },
  { color: '#A6720A', backgroundColor: '#FAF0DC' },
  { color: '#B81E8C', backgroundColor: '#FBE6F4' },
  { color: '#0F7B84', backgroundColor: '#E2F2F3' },
];

export const isShipDateFilterOn = (selectedShipWindows?: string[] | null) =>
  !!selectedShipWindows && selectedShipWindows.length > 0;

export const matchesShipWindows = (item: any, selectedWindowKeys?: Set<string> | null): boolean =>
  !selectedWindowKeys ||
  selectedWindowKeys.has(getShipWindowKey(item?.shippingStartDate, item?.shippingEndDate));

// the same item can be elected per warehouse, so a window of one warehouse is not a window of another
export const getShipWindowItemKey = (item: any) => `${item?.itemNumber}|${item?.warehouse ?? ''}`;

// keyed by the window itself, so only rows on the very same dates share a colour
export const getShipWindowColorIndexByWindow = (items?: any[] | null) => {
  const colorIndexByWindow: { [windowKey: string]: number } = {};
  getUniqueShipWindows(items).forEach((shipWindow, index) => {
    colorIndexByWindow[shipWindow.key] = index % shipWindowPalette.length;
  });
  return colorIndexByWindow;
};

export const shipWindowComparator = (valueA: string, valueB: string, nodeA: any, nodeB: any) =>
  getShipWindowKey(nodeA?.data?.shippingStartDate, nodeA?.data?.shippingEndDate).localeCompare(
    getShipWindowKey(nodeB?.data?.shippingStartDate, nodeB?.data?.shippingEndDate)
  );
